import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import { cssVar } from '@feathr/hooks';

import {
  formatAxisDate,
  formatCountTooltipValue,
  type IViewsChartRow,
} from './DonationFormTrends.utils';
import type { ITrendChartLegendEntry } from './TrendChartFrame';
import TrendChartFrame from './TrendChartFrame';

export interface IViewsOverTimeChartProps {
  /** Chart-ready rows produced by `buildViewsChartData`. */
  data: IViewsChartRow[];
}

/**
 * Views Over Time — recharts LineChart overlaying four series: total views
 * (current + previous) as solid lines and unique views (current + previous)
 * as dashed lines. Current is sky-600, previous is neutral-500. Current lines
 * are rendered last so they win z-order at intersections.
 */
function ViewsOverTimeChart({ data }: Readonly<IViewsOverTimeChartProps>): JSX.Element {
  const { t } = useTranslation();
  const currentColor = cssVar('--color-sky-600');
  const previousColor = cssVar('--color-neutral-500');

  const legend: ITrendChartLegendEntry[] = [
    { label: t('Total views (Current)'), color: currentColor },
    { label: t('Total views (Previous)'), color: previousColor },
    { label: t('Unique views (Current)'), color: currentColor, dashed: true },
    { label: t('Unique views (Previous)'), color: previousColor, dashed: true },
  ];

  return (
    <TrendChartFrame
      dataName={'views-over-time-chart'}
      legend={legend}
      legendDataName={'views-over-time-legend'}
      subtitle={t('Total views vs. unique viewers for the selected period')}
      title={t('Views Over Time')}
    >
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid
          stroke={cssVar('--color-decorative-secondary')}
          strokeDasharray={'4 4'}
          vertical={false}
        />
        <XAxis
          axisLine={false}
          dataKey={'date'}
          interval={'preserveStartEnd'}
          minTickGap={24}
          tickFormatter={formatAxisDate}
          tickLine={false}
        />
        <YAxis axisLine={false} tickLine={false} width={48} />
        <Tooltip formatter={formatCountTooltipValue} labelFormatter={formatAxisDate} />
        <Line
          dataKey={'totalPrevious'}
          dot={false}
          name={t('Total views (Previous)')}
          stroke={previousColor}
          strokeWidth={2}
          type={'monotone'}
        />
        <Line
          dataKey={'uniquePrevious'}
          dot={false}
          name={t('Unique views (Previous)')}
          stroke={previousColor}
          strokeDasharray={'4 4'}
          strokeWidth={2}
          type={'monotone'}
        />
        <Line
          dataKey={'totalCurrent'}
          dot={false}
          name={t('Total views (Current)')}
          stroke={currentColor}
          strokeWidth={2}
          type={'monotone'}
        />
        <Line
          dataKey={'uniqueCurrent'}
          dot={false}
          name={t('Unique views (Current)')}
          stroke={currentColor}
          strokeDasharray={'4 4'}
          strokeWidth={2}
          type={'monotone'}
        />
      </LineChart>
    </TrendChartFrame>
  );
}

export default ViewsOverTimeChart;
